import type {PackageCategoryEntity} from 'types/entities/PackageCategoryEntity'
import {PackageCategory} from 'types/enums/PackageCategory'
import {isOfferCategoryAvailableForParticipants} from './offerCategoryEligibility'
import type {PackagesCart} from 'types/entities/PackagesCart'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'
import type {Offer} from 'types/entities/Offer'
import type {PackagesItemsResponse} from 'types/entities/PackagesItems'

/** The AJAX interceptor rejects with a response, while other callers may reject with an Axios error. */
export const getOfferPackageSwitchFailure = (error: unknown): {message?: string; status?: number} => {
    if (!error || typeof error !== 'object') return {}
    const failure = error as {response?: {data?: unknown; status?: number}; data?: unknown; status?: number; message?: unknown}
    const data = failure.response?.data ?? failure.data ?? error
    const serverMessage = data && typeof data === 'object' ? (data as {message?: unknown}).message : undefined
    const message = typeof serverMessage === 'string' && serverMessage.trim()
        ? serverMessage.trim()
        : typeof failure.message === 'string' && failure.message.trim() ? failure.message.trim() : undefined
    return {message, status: failure.response?.status ?? failure.status}
}

export const getSwitchableOfferCalculations = (offer: Offer) =>
    (offer.calculations ?? []).filter((calculation) => !calculation.deleted_at &&
        calculation.packagesCart?.id && calculation.packagesCart.token &&
        getOfferPackageSwitchItemIds(calculation.packagesCart).length > 0)

/** Source ticket IDs identify whether a calculation contains access tickets. */
export const getOfferPackageSwitchItemIds = (cart?: PackagesCart | null): number[] =>
    (cart?.packagesCartItems ?? [])
        .filter((item) => item.packageItem?.type === PackageItemItemType.PackageItem &&
            item.drivers_count_adult + item.drivers_count_child > 0)
        .map((item) => item.package_item_id)
        .filter((id) => Number.isInteger(id) && id > 0)

/** The endpoint groups replacements by duration and uses the first item in each group. */
export const resolveOfferPackageReplacementItems = (cart: PackagesCart, catalog: PackagesItemsResponse) => {
    const accessItems = cart.packagesCartItems.filter((item) => item.packageItem?.type === PackageItemItemType.PackageItem)
    const durations = [...new Set(accessItems.map((item) => item.packageItem.duration))]
    const available = [...(catalog.packageItems.adult ?? []), ...(catalog.packageItems.child ?? [])]
        .filter((item) => item.type === PackageItemItemType.PackageItem && Number.isInteger(item.id) && item.id > 0)
    const itemIds: number[] = []
    const missingDurations: number[] = []
    for (const duration of durations) {
        const match = Number.isFinite(duration) && duration > 0 ? available.find((item) => item.duration === duration) : undefined
        if (match) itemIds.push(match.id)
        else missingDurations.push(duration)
    }
    return {itemIds, missingDurations}
}

/** Apply the offer catalog's restrictions using the source calculation, not a booking draft. */
export const isSwitchCategoryAvailable = (category: PackageCategoryEntity, date: string, adults: number, children: number, sourceCategory?: PackageCategory): boolean => {
    // Existing Family carts can lack child counts. Allow the analogous Kids targets
    // without inventing participants; the dated catalog and backend still validate them.
    const isFamilyToKids = sourceCategory === PackageCategory.Action &&
        (category.id === PackageCategory.HappyKids || category.id === PackageCategory.HappyKidsPlus)
    if ((!category.enable && category.id !== PackageCategory.School) || category.deprecated ||
        (!isFamilyToKids && !isOfferCategoryAvailableForParticipants(category.id, adults, children))) return false
    const day = new Date(`${date.slice(0, 10)}T00:00:00`)
    if (!Number.isFinite(day.getTime())) return false
    const {releaseDate, expiryDate} = category.extra ?? {}
    const boundary = (value: string | Date) => {
        const result = new Date(value)
        result.setHours(0, 0, 0, 0)
        return result
    }
    return !(releaseDate && day < boundary(releaseDate)) && !(expiryDate && day > boundary(expiryDate))
}
